import { z } from 'zod';
import { CASE_ORIGINS, CASE_PRIORITIES, CASE_STATUSES } from '../enums.js';
import { optionalText, paginationQuerySchema, toPatchSchema, uuidSchema } from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * Support tickets. Zoho's Cases module.
 *
 * `caseNumber` is not accepted from the client: it is allocated server-side
 * per organization, because two people opening a case at once must not be able
 * to claim the same number.
 */
export const createCaseSchema = z.object({
  subject: z.string().trim().min(1, 'Required').max(300),
  status: z.enum(CASE_STATUSES).default('NEW'),
  priority: z.enum(CASE_PRIORITIES).default('MEDIUM'),
  origin: z.enum(CASE_ORIGINS).optional(),
  reason: optionalText(200),
  type: optionalText(120),

  description: optionalText(8000),
  /** Not shown to the customer on any portal; kept apart from `description`. */
  internalComments: optionalText(8000),
  resolution: optionalText(8000),

  accountId: uuidSchema.nullish(),
  contactId: uuidSchema.nullish(),
  productId: uuidSchema.nullish(),

  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateCaseInput = z.infer<typeof createCaseSchema>;

export const updateCaseSchema = toPatchSchema(createCaseSchema);
export type UpdateCaseInput = z.infer<typeof updateCaseSchema>;

export const CASE_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'caseNumber',
  'priority',
  'status',
] as const;

export const listCasesQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(CASE_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  status: z.enum(CASE_STATUSES).optional(),
  priority: z.enum(CASE_PRIORITIES).optional(),
  accountId: uuidSchema.optional(),
  /** Everything not closed — the default working view. */
  openOnly: z.stringbool().optional(),
});
export type ListCasesQuery = z.infer<typeof listCasesQuerySchema>;

export interface CaseSummary {
  id: string;
  caseNumber: string;
  subject: string;
  status: (typeof CASE_STATUSES)[number];
  priority: (typeof CASE_PRIORITIES)[number];
  origin: (typeof CASE_ORIGINS)[number] | null;
  account: RecordRef | null;
  contact: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CaseDetail extends CaseSummary {
  reason: string | null;
  type: string | null;
  description: string | null;
  internalComments: string | null;
  resolution: string | null;
  product: RecordRef | null;
  customFields: Record<string, unknown>;
}
